/**
 * 149-da (PROMPT 16-ACC-LEGACY-DISPLAY-R1): Pure JS mirror of backend
 * `_classify_income_entry` in server.py. Applied on the client to determine
 * how legacy income_entries should be DISPLAYED — no DB mutation.
 *
 * Rules (identical to backend 149-cw):
 *   1. INVALID_OR_INCOMPLETE  — amount ≤ 0 | missing project/client |
 *                                DPP+PPN != gross (>Rp1) | pph_amount>0 pph_pct=0
 *   2. RETENTION_RELATED      — is_retention_claim === true
 *   3. WAITING_SAFE           — date is null/empty (no proof of receipt)
 *   4. PROVEN_PAID            — date + bank + acct + note markers eksplisit
 *   5. AMBIGUOUS_RECEIVED_DATE — date filled without proven-paid markers
 *
 * DUPLICATE_CANDIDATE is NOT computable per-entry (needs global scan) — the
 * frontend treats duplicates as AMBIGUOUS for display purposes; only the
 * backend audit endpoint marks them explicitly.
 *
 * DO NOT modify these rules without also updating server.py:_classify_income_entry.
 */

const PROVEN_MARKERS = [
  "transfer masuk", "sudah masuk", "sudah diterima", "sudah lunas",
  "diterima tanggal", "tt masuk", "bukti transfer", "payment received",
];

export const LEGACY_CATEGORIES = Object.freeze({
  INVALID_OR_INCOMPLETE: "INVALID_OR_INCOMPLETE",
  RETENTION_RELATED: "RETENTION_RELATED",
  WAITING_SAFE: "WAITING_SAFE",
  PROVEN_PAID: "PROVEN_PAID",
  AMBIGUOUS_RECEIVED_DATE: "AMBIGUOUS_RECEIVED_DATE",
});

/**
 * @param {object} entry — income_entry doc from active_projects.income_entries[]
 * @param {object} project — parent project (must have id + client)
 * @returns {string} one of LEGACY_CATEGORIES
 */
export function classifyIncomeEntry(entry, project) {
  const amount = Number(entry?.amount) || 0;
  const invalid = [];
  if (amount <= 0) invalid.push("amount");
  if (!project?.id) invalid.push("project_id");
  if (!(project?.client || "").trim()) invalid.push("client");
  const dpp = Number(entry?.dpp_amount) || 0;
  const vat = Number(entry?.vat_amount) || 0;
  const gross = Number(entry?.gross_invoice_amount) || 0;
  if (dpp > 0 && gross > 0 && Math.abs((dpp + vat) - gross) > 1) invalid.push("dpp_vat_gross");
  const pphAmt = Number(entry?.pph_amount) || 0;
  const pphPct = Number(entry?.pph_pct) || 0;
  if (pphAmt > 0 && pphPct <= 0) invalid.push("pph_double_deduct");
  if (invalid.length) return LEGACY_CATEGORIES.INVALID_OR_INCOMPLETE;

  if (entry?.is_retention_claim) return LEGACY_CATEGORIES.RETENTION_RELATED;

  if (!entry?.date) return LEGACY_CATEGORIES.WAITING_SAFE;

  const note = String(entry?.note || "").toLowerCase();
  const hasBank = Boolean(String(entry?.bank_name || "").trim());
  const hasAcct = Boolean(String(entry?.account_no || "").trim());
  const hasProvenNote = PROVEN_MARKERS.some((m) => note.includes(m));
  if (hasBank && hasAcct && hasProvenNote) return LEGACY_CATEGORIES.PROVEN_PAID;

  return LEGACY_CATEGORIES.AMBIGUOUS_RECEIVED_DATE;
}

/**
 * Display-status helper — maps a classification into a UI-facing label + tone.
 * All consumers (Ledger, AR, Summary, Dashboard, exports) should use this so
 * status labels stay consistent.
 */
export function getEntryDisplayStatus(entry, project) {
  const cat = classifyIncomeEntry(entry, project);
  switch (cat) {
    case LEGACY_CATEGORIES.PROVEN_PAID:
      return {
        category: cat,
        label: "Diterima",
        tone: "border-emerald-400 bg-emerald-50 text-emerald-700",
        tooltip: "Pembayaran terverifikasi berdasarkan bukti transfer.",
      };
    case LEGACY_CATEGORIES.AMBIGUOUS_RECEIVED_DATE:
      return {
        category: cat,
        label: "Perlu Verifikasi",
        tone: "border-amber-400 bg-amber-50 text-amber-700",
        tooltip: "Tanggal terisi tetapi belum ada bukti transfer eksplisit. Estimasi Net berasal dari formula (DPP + PPN − PPh − retensi), BUKAN uang aktual yang diterima.",
      };
    case LEGACY_CATEGORIES.INVALID_OR_INCOMPLETE:
      return {
        category: cat,
        label: "Data Tidak Lengkap",
        tone: "border-rose-400 bg-rose-50 text-rose-700",
        tooltip: "Data project/client hilang atau DPP+PPN tidak konsisten dengan gross. Butuh perbaiki data source dulu.",
      };
    case LEGACY_CATEGORIES.RETENTION_RELATED:
      return {
        category: cat,
        label: "Klaim Retensi",
        tone: "border-purple-400 bg-purple-50 text-purple-700",
        tooltip: "Klaim retensi — bukan pembayaran invoice reguler.",
      };
    case LEGACY_CATEGORIES.WAITING_SAFE:
    default:
      return {
        category: LEGACY_CATEGORIES.WAITING_SAFE,
        label: "Menunggu Pembayaran",
        tone: "border-amber-400 bg-amber-50 text-amber-700",
        tooltip: "Belum ada tanggal penerimaan — actual_received = Rp0.",
      };
  }
}

/**
 * Compute a project's payment KPIs, splitting VERIFIED actual received (from
 * PROVEN_PAID only) vs LEGACY formula-only estimate (from AMBIGUOUS).
 * INVALID entries excluded from both. Retention accumulated separately.
 *
 * Callers should display:
 *   • actualReceivedVerified (Rp) — real money received
 *   • estNetLegacy (Rp) — formula-only estimate for AMBIGUOUS entries
 *   • needsVerificationCount / needsVerificationTotal — count+Rp of AMBIGUOUS
 *   • incompleteCount / incompleteTotal — count+Rp of INVALID
 */
export function computeLegacyKPIs(project) {
  const entries = project?.income_entries || [];
  let actualReceivedVerified = 0;
  let estNetLegacy = 0;
  let totalInvoiceDpp = 0;
  let totalGross = 0;
  let totalRetention = 0;
  let needsVerificationCount = 0;
  let needsVerificationTotal = 0;
  let incompleteCount = 0;
  let incompleteTotal = 0;
  let retentionClaimTotal = 0;

  for (const e of entries) {
    const cat = classifyIncomeEntry(e, project);
    const dpp = Number(e.dpp_amount || e.amount || 0);
    const gross = Number(e.gross_invoice_amount || 0) || dpp;
    const net = Number(e.net_received_amount || 0);
    const ret = Number(e.retention_amount || 0);
    const amt = Number(e.amount || 0);

    if (cat === LEGACY_CATEGORIES.RETENTION_RELATED) {
      retentionClaimTotal += amt;
      continue;
    }
    if (cat === LEGACY_CATEGORIES.INVALID_OR_INCOMPLETE) {
      incompleteCount += 1;
      incompleteTotal += amt;
      continue;
    }
    totalInvoiceDpp += dpp;
    totalGross += gross;
    totalRetention += ret;
    if (cat === LEGACY_CATEGORIES.PROVEN_PAID) {
      actualReceivedVerified += net || gross;
    } else if (cat === LEGACY_CATEGORIES.AMBIGUOUS_RECEIVED_DATE) {
      needsVerificationCount += 1;
      needsVerificationTotal += amt;
      estNetLegacy += net || (gross - Number(e.income_tax_amount || 0) - ret);
    }
    // WAITING_SAFE contributes to totalInvoiceDpp/totalGross but NOT to received.
  }
  const contract = Number(project?.contract_value) || 0;
  return {
    contract,
    totalInvoiceDpp,
    totalGross,
    totalRetention,
    actualReceivedVerified,
    estNetLegacy,
    needsVerificationCount,
    needsVerificationTotal,
    incompleteCount,
    incompleteTotal,
    retentionClaimTotal,
  };
}
